import { localizeItemName } from '../i18n/content';
import { type ActionTooltipContent } from './ActionTooltip';

function resolveLocalizedText(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const localized = value as Record<string, unknown>;
  const candidates = [localized.en, ...Object.values(localized)];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  return null;
}

export function parseItemInputs(metadata: Record<string, unknown>): string[] {
  const rawInputs = metadata.inputs_json;
  if (typeof rawInputs !== 'string') {
    return [];
  }
  try {
    const parsed = JSON.parse(rawInputs) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    return [];
  }
}

export function mergeItemMetadata(
  slotMetadata: Record<string, unknown>,
  catalogMetadata?: Record<string, unknown>
): Record<string, unknown> {
  if (!catalogMetadata) {
    return slotMetadata;
  }
  return {
    ...catalogMetadata,
    ...slotMetadata,
  };
}

export function createItemTooltipContent(
  itemId: string,
  quantity: number,
  metadata: Record<string, unknown>,
  options?: { catalogMetadata?: Record<string, unknown>; tradeable?: boolean }
): ActionTooltipContent {
  const mergedMetadata = mergeItemMetadata(metadata, options?.catalogMetadata);
  const itemName = localizeItemName(itemId, mergedMetadata, itemId);
  const metadataDescription = resolveLocalizedText(mergedMetadata.description_i18n)
    ?? (typeof mergedMetadata.description === 'string' ? mergedMetadata.description : null)
    ?? null;

  const lines: string[] = [];
  const inputs = parseItemInputs(mergedMetadata);
  lines.push(`Inputs: ${inputs.length > 0 ? inputs.join(', ') : 'none'}`);

  const tags = Array.isArray(mergedMetadata.tags)
    ? mergedMetadata.tags.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0)
    : [];
  if (tags.length > 0) {
    lines.push(`Tags: ${tags.join(', ')}`);
  }

  return {
    title: itemName,
    titleSuffix: options?.tradeable === false ? '(untradeable)' : '(tradeable)',
    description: metadataDescription,
    statLines: lines,
  };
}
